import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { DayCard } from '@/features/meal-plan/DayCard'
import { RecipePicker } from '@/features/meal-plan/RecipePicker'
import { useMealPlan } from '@/features/meal-plan/useMealPlan'
import { useAssignMealSlot } from '@/features/meal-plan/useAssignMealSlot'
import { useUpdateServingMultiplier } from '@/features/meal-plan/useUpdateServingMultiplier'
import { useRecipes } from '@/features/recipes/useRecipes'
import { getMondayOfWeek, DAY_NAMES } from '@/features/meal-plan/utils'
import type { MealSlot } from '@/features/meal-plan/types'

interface Props {
  householdSize: number | null
  onGenerate: (filledSlots: number) => void
}

export function OnboardingPlanStep({ householdSize, onGenerate }: Props) {
  const { t } = useTranslation('onboarding')
  const weekStartDate = getMondayOfWeek()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [activeDayOfWeek, setActiveDayOfWeek] = useState<number | null>(null)
  const [autoFillDone, setAutoFillDone] = useState(false)

  const { data: mealPlan, isLoading } = useMealPlan(weekStartDate)
  const { data: recipes } = useRecipes()
  const assignMutation = useAssignMealSlot(weekStartDate)
  const servingMutation = useUpdateServingMultiplier(weekStartDate)

  const filledSlots = mealPlan?.slots.filter((s) => !!s.recipeId && !s.isSuggestion).length ?? 0

  const handleSlotTap = (dayOfWeek: number) => {
    setActiveDayOfWeek(dayOfWeek)
    setPickerOpen(true)
  }

  const handleRecipeSelect = (recipeId: string) => {
    if (activeDayOfWeek === null) return
    assignMutation.mutate({ weekStartDate, dayOfWeek: activeDayOfWeek, recipeId })
    setPickerOpen(false)
  }

  const handleAutoFill = () => {
    if (!recipes || recipes.length === 0) return

    const availableRecipes = recipes.slice(0, 7)
    const multiplier = householdSize && householdSize > 1 ? householdSize : 1

    availableRecipes.forEach((recipe, index) => {
      assignMutation.mutate(
        { weekStartDate, dayOfWeek: index, recipeId: recipe.id },
        {
          onSuccess: () => {
            if (multiplier > 1) {
              servingMutation.mutate({
                weekStartDate,
                dayOfWeek: index,
                servingMultiplier: multiplier,
              })
            }
          },
        },
      )
    })

    setAutoFillDone(true)
  }

  const hasRecipes = recipes && recipes.length > 0

  return (
    <div className="flex flex-col flex-1">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-ink">{t('plan.heading')}</h1>
        <p className="text-sm text-muted mt-1">{t('plan.subtitle')}</p>
      </div>
      <div className="flex-1">
        {hasRecipes && !autoFillDone && (
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAutoFill}
              className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition"
            >
              {t('plan.autoFill')}
            </button>
            <p className="text-xs text-muted text-center mt-1">{t('plan.autoFillDescription')}</p>
          </div>
        )}
        {isLoading && (
          <div role="status" aria-label="Loading meal plan" className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-line border-t-accent" />
          </div>
        )}
        {mealPlan && (
          <div className="flex flex-col gap-3 md:grid md:grid-cols-7 md:gap-4">
            {mealPlan.slots.map((slot: MealSlot) => (
              <DayCard
                key={slot.dayOfWeek}
                slot={slot}
                dayName={DAY_NAMES[slot.dayOfWeek] ?? ''}
                userRole="Planner"
                onSlotTap={() => handleSlotTap(slot.dayOfWeek)}
              />
            ))}
          </div>
        )}
        <RecipePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={handleRecipeSelect}
        />
      </div>
      <div className="pt-4 pb-2">
        <button
          type="button"
          disabled={filledSlots === 0}
          onClick={() => onGenerate(filledSlots)}
          className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('plan.generate')}
        </button>
      </div>
    </div>
  )
}
